import React from "react";
import { connect } from "react-redux";

import { ProductCompareTheme } from "../../../constants";
import { IProduct } from "../../../models/catalogue.interfaces";
import { ISafeHTML, IWebPageURL } from "../../../models/nominals";
import {
    IAttributeOptionGroup,
    ICompareTile,
} from "../../../models/product-compare.interfaces";
import { IViewport } from "../../../models/screen.interfaces";
import { trackCategoryPageView } from "../../../utils/analytics";
import { notEmpty } from "../../../utils/functional";
import { rootProductSelector } from "../../configurator/selectors";
import { TDispatchMapper, TStateMapper } from "../../reducers.interfaces";
import { ProductCompareGrid as ProductCompareGridComponent } from "../components/ProductCompareGrid";
import { defaults } from "../defaults";
import { Dispatchers } from "../dispatchers";
import { Loaders } from "../loaders";
import { getSelectedVariant } from "../selectors";

interface IOwnProps {
    header: ISafeHTML;
    basketLink: IWebPageURL;
    financingLink: IWebPageURL;
    preselectedSize: string | null;
    sizeAttributeOptionGroup: IAttributeOptionGroup | null;
    tiles: ICompareTile[];
    theme: ProductCompareTheme;
    showFirstAvailablePrice?: boolean;
    showRating?: boolean;
    showStartingAt?: boolean;
    isMinimal?: boolean;
}

interface IReduxProps {
    priceSelectValue: string;
    viewport: IViewport;
    rootProduct: IProduct | null;
    rootProducts: IProduct[];
}

interface IDispatchProps {
    loaders: Loaders;
    dispatchers: Dispatchers;
}

interface IProps extends IOwnProps, IDispatchProps, IReduxProps {}

interface IState {}

// TODO(ssr, LEVEL 7 out of 10): Migrate this component from class-style to functional-style. Rated LOE as 7/10 due to Redux connect, componentDidUpdate lifecycle with analytics tracking, and multiple selector dependencies.
class ProductCompareGridContainer extends React.Component<IProps, IState> {
    private trackCategoryView() {
        if (!this.props.tiles) {
            return;
        }
        // Obtain SKUs for current variants
        const skus = this.props.tiles
            .map((tile) => {
                const variant = getSelectedVariant(
                    tile,
                    this.props.priceSelectValue,
                );
                return variant ? variant.skus[0] : null;
            })
            .filter(notEmpty);
        trackCategoryPageView(skus);
    }

    componentDidUpdate() {
        this.trackCategoryView();
    }

    render() {
        const urlParams = new URLSearchParams(window.location.search);
        const pageRootProduct = this.props.rootProduct
            ? this.props.rootProduct.id
            : null;
        return (
            <ProductCompareGridComponent
                header={this.props.header}
                tiles={this.props.tiles}
                basketLink={this.props.basketLink}
                financingLink={this.props.financingLink}
                sizeAttributeOptionGroup={this.props.sizeAttributeOptionGroup}
                preselectedSize={this.props.preselectedSize || null}
                priceSelectValue={this.props.priceSelectValue}
                pageRootProduct={pageRootProduct}
                theme={this.props.theme}
                urlParams={urlParams}
                viewport={this.props.viewport}
                showFirstAvailablePrice={this.props.showFirstAvailablePrice}
                showRating={this.props.showRating}
                showStartingAt={this.props.showStartingAt}
                setRootProducts={this.props.dispatchers.setRootProducts}
                rootProducts={this.props.rootProducts}
                isMinimal={this.props.isMinimal}
            />
        );
    }
}

const mapStateToProps: TStateMapper<
    "productcompare" | "configurator",
    IReduxProps,
    IOwnProps
> = (rootState, ownProps) => {
    const state = rootState.productcompare || defaults;
    const rootProduct = rootProductSelector(rootState.configurator);

    return {
        ...ownProps,
        viewport: rootState.common.viewport,
        priceSelectValue: state.priceSelectValue,
        rootProduct: rootProduct,
        rootProducts: state.rootProducts,
    };
};

const mapDispatchToProps: TDispatchMapper<IDispatchProps> = (dispatch) => {
    const dispatchers = new Dispatchers(dispatch);
    const loaders = new Loaders(dispatchers);

    return {
        dispatchers: dispatchers,
        loaders: loaders,
    };
};

export const ProductCompareGrid = connect(
    mapStateToProps,
    mapDispatchToProps,
)(ProductCompareGridContainer);
